import { Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { connect } from 'react-redux';
import { useRef ,useEffect, useState} from 'react';
import { DARK_MODE,PART_VALIDATION_ERROR } from '../../constants';
import {
	getPartValidationDetailsForSerialNumber,
	revalidatePart,
	updatePartValidation,
	validatePart,
} from '../../redux/actionCreators/partValidation';
import { useResolutionScale } from '../../hooks';
import ModalDialog from '../../components/ModalDialog/ModalDialog';
import StyledTable from '../StyledTable/StyledTable';
import { useTorqueColumns } from '../TorqueTable/useTorqueColumns';
import { getPartFamiliesToValidate } from '../../utils/utils';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';

// ---------------------------
// Helper Functions
// ---------------------------
const getRowBackgroundColor = (status, themeMode) => {
	const isDark = themeMode === DARK_MODE;

	switch (status) {
		case 'Validated':
			return isDark ? 'rgba(30, 191, 54, 0.4)' : 'rgba(30, 191, 54, 0.4)';
		case 'Skipped':
			return isDark ? 'rgba(203, 29, 73, 0.4)' : 'rgba(203, 29, 73, 0.4)';
		case 'Pending':
			return isDark ? 'rgba(236, 176, 64, 0.4)' : 'rgba(236, 176, 64, 0.4)';
		default:
			return isDark ? '#1A2127' : '#F8F8F8';
	}
};

const getRowHoverColor = (status, themeMode) => {
	const isDark = themeMode === DARK_MODE;

	switch (status) {
		case 'Validated':
			return isDark ? 'rgba(30, 191, 54, 0.35)' : 'rgba(30, 191, 54, 0.55)';
		case 'Skipped':
			return isDark ? 'rgba(203, 29, 73, 0.35)' : 'rgba(203, 29, 73, 0.55)';
		case 'Pending':
			return isDark ? 'rgba(236, 176, 64, 0.35)' : 'rgba(236, 176, 64, 0.55)';
		default:
			return isDark ? '#232B31' : '#EDEDED';
	}
};

// Bright color for Status column
const getStatusTextColor = (status, themeMode) => {
	const isDark = themeMode === DARK_MODE;
	switch (status) {
		case 'Validated':
			return isDark ? '#86FF9A' : '#229104';
		case 'Skipped':
			return isDark ? '#FF91A5' : '#CB1D49';
		case 'Pending':
			return isDark ? '#FFD885' : '#ECB040';
		default:
			return isDark ? '#E6E6E6' : '#202020';
	}
};

// Softer complementary tone for other columns
const getOtherTextColor = (status, themeMode) => {
	const isDark = themeMode === DARK_MODE;
	switch (status) {
		case 'Validated':
			return isDark ? '#3BFF6D' : '#1F6029';
		case 'Skipped':
			return isDark ? '#FF577A' : '#641F30';
		case 'Pending':
			return isDark ? '#CBA24F' : '#725A2D';
		default:
			return isDark ? '#FFFFFF' : '#202020';
	}
};

// ---------------------------
// Dot Indicator
// ---------------------------
const DotIndicator = styled(Box)(({ active }) => ({
	width: active ? 10 : 8,
	height: active ? 10 : 8,
	borderRadius: '50%',
	margin: '6px 0',
	backgroundColor: active ? '#229104' : '#B0B0B0',
	transition: 'all 0.2s ease',
}));

// ---------------------------
// Scroll Container + Pages
// ---------------------------
const ScrollContainer = styled(Box)(() => ({
	maxHeight: '60vh', // subtract header + padding space
	overflowY: 'auto',
	scrollSnapType: 'y mandatory',
	scrollBehavior: 'smooth',
	paddingRight: '12px', // avoid scrollbar overlap with content
	paddingBottom: '12px',
	marginBottom: '0', // ensure it doesn’t push beyond modal
	boxSizing: 'border-box',
}));

const Page = styled(Box)(() => ({
	scrollSnapAlign: 'start',
	minHeight: '70vh',
	paddingRight: '24px',
	boxSizing: 'border-box',
}));

// ---------------------------
// Dynamic Data Columns
// ---------------------------
const getSummaryColumns = () => [
	{
		field: 'scannedSerialNo',
		title: 'VIN',
		flex: 1,
		align: 'center',
	},
	{
		field: 'casePackNumber',
		title: 'Case Pack Number',
		flex: 1,
		align: 'center',
	},
	{
		field: 'casePackDescription',
		title: 'Case Pack Description',
		flex: 1.5,
		align: 'center',
	},
];

const getDetailsColumns = () => [
	{ field: 'partName', title: 'Part Name', sortable: true, flex: 1 },
	{ field: 'partNumber', title: 'Part Number', sortable: true, flex: 1 },
	{
		field: 'partDescription',
		title: 'Part Description',
		sortable: true,
		flex: 1.5,
	},
	{ field: 'qty', title: 'Qty', sortable: true, flex: 0.5, align: 'center' },
	{
		field: 'serialNumber',
		title: 'Serial Number',
		label: 'Serial Number',
		sortable: true,
		flex: 1,
		align: 'center',
		render: (row) => {
			// Get scanned serial numbers from scanJobResults
			const serialNumber = row.status === 'Skipped'? '-' : row.serialNumber;
			return (
				<Typography
					sx={{
						fontFamily: "'Uni Neue', sans-serif",
						fontWeight: 800,
						textAlign: 'center',
					}}
				>
					{serialNumber}
				</Typography>
			);
		},
	},
	{
		field: 'status',
		title: 'Status',
		sortable: true,
		flex: 0.7,
		align: 'center',
	},
];

const mapStateToProps = (state) => {
	return {
		receivePart: state.receivePart,
		partValidation: state.partValidation,
		themeMode: state.theme.themeMode,
		operations: state.operations.allowedOperations,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getPartValidationDetailsForSerialNumber: (
		successCallback,
		errorCallback,
		sequenceNumber,
	) =>
		dispatch(
			getPartValidationDetailsForSerialNumber(
				successCallback,
				errorCallback,
				sequenceNumber,
			),
		),
	updatePartValidation: (data) => dispatch(updatePartValidation(data)),
	revalidatePart: (
		sequenceNumber,
		idToUpdate,
		payload,
		successCallback,
		errorCallback,
	) =>
		dispatch(
			revalidatePart(
				sequenceNumber,
				idToUpdate,
				payload,
				successCallback,
				errorCallback,
			),
		),
	validatePart: (sequenceNumber, payload, successCallback, errorCallback) =>
		dispatch(
			validatePart(sequenceNumber, payload, successCallback, errorCallback),
		),
});

const RescanModal = ({
	receivePart,
	partValidation,
	getPartValidationDetailsForSerialNumber,
	themeMode,
	open,
	handleClose,
	rows, // Add rows prop for dynamic data
	scannableParts, // Add scannableParts prop for dynamic data
	showTorqueTable = true, // Add prop to control torque table visibility
	torqueData = [],
	operations,
}) => {
	const scrollRef = useRef(null);
	const columns = useTorqueColumns();
	const { showMessageOverlay } = useMessageContextManager();
	const [tableRows, setTableRows] = useState(null);

	// Use dynamic data from props instead of hardcoded data
	const summaryData = rows?.[0]
		? {
				scannedSerialNo:
					rows[0].serialNumber || receivePart.serialNumber || '-',
				casePackNumber: rows[0].parentPartNumber || '-',
				casePackDescription: rows[0].parentPartDescription || '-',
		  }
		: {
				scannedSerialNo: '-',
				casePackNumber: '-',
				casePackDescription: '-',
		  };

	// Use scannableParts for details data if available, otherwise use tableRows
	const detailsData = scannableParts || tableRows || [];

	const mappedData = detailsData.map((part, i) => {
		return {
			id: i,
			partName: part.familyName || '-',
			partNumber: part.partNumber || '-',
			partDescription: part.partDescription || '-',
			qty: part.quantity || '1',
			serialNumber: part.scanJobResults?.[0]?.scannedSerialNumber || '-',
			status: part.scanJobResults?.[0]?.skipReason
				? 'Skipped'
				: part.scanJobResults?.length >= part.quantity
				? 'Validated'
				: 'Pending',
		};
	});

useEffect(() => {
    if (receivePart.serialNumber) {
        const isScanAttributeOnly = operations?.includes('SCAN_ATTRIBUTE') &&
                                    !operations?.includes('PART_VALIDATION');
        if (isScanAttributeOnly) return;

        getPartValidationDetailsForSerialNumber(
            (data) => {
                const rowsData = getPartFamiliesToValidate(
                    data.scanJobDetails,
                    data.sequenceNumber,
                );
                setTableRows(rowsData);
            },
            (error) => {
                updatePartValidation({});
                showMessageOverlay({
                    contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Get Part Validation Details',
                    info: error?.response?.data?.message,
                    error: true,
                });
            },
            receivePart.serialNumber,
        );
    }
}, [receivePart.serialNumber]);

	const { scaleValue } = useResolutionScale();

	if (!partValidation || !Object.keys(partValidation)?.length) return null;

	return (
		<ModalDialog
			open={open}
			title="Part Validation Report"
			onClose={handleClose}
		>
			<Box
				sx={{
					height: '150px',
					position: 'relative',
					width: '90%',
					margin: '0 auto',
				}}
			>
				{/* Scrollable Container */}
				<ScrollContainer ref={scrollRef}>
					{/* ---------- Page 1: Summary + Details ---------- */}
					<Page>
						{/* Summary Table */}
						<StyledTable
							title="" // no title since modal already has one
							columns={getSummaryColumns()}
							rows={[{ ...summaryData }]}
							elevated={false}
							sx={{
								'& .MuiTableCell-root': {
									fontFamily: "'Uni Neue', sans-serif",
									fontWeight: 800,
									fontSize: `${scaleValue(20)}px`,
									border: 'none',
								},
								tableColors: {
									headerText: themeMode === DARK_MODE ? '#F8F8F8' : '#202020',
									headerBackground: 'transparent',
								},
							}}
						/>

						{/* Part Detail Table */}
						<Grid
							sx={{ display: 'flex', flexDirection: 'column', marginTop: 1 }}
						>
							<StyledTable
								title=""
								columns={getDetailsColumns()}
								rows={mappedData}
								elevated={false}
								sx={{
									flex: 1,
									tableColors: {
										headerText: themeMode === DARK_MODE ? '#F8F8F8' : '#202020',
										headerBackground: 'transparent',
									},
								}}
								rowStyle={(row, themeMode) => {
									const baseColor = getRowBackgroundColor(
										row.status,
										themeMode,
									);
									const hoverColor = getRowHoverColor(row.status, themeMode);
									return {
										backgroundColor: baseColor,
										borderBottom: `1px solid ${hoverColor}`,
										'&:hover': {
											backgroundColor: hoverColor,
										},
									};
								}}
								cellStyle={(row, col, themeMode) => ({
									color:
										col.field === 'status'
											? getStatusTextColor(row.status, themeMode)
											: getOtherTextColor(row.status, themeMode),
									textAlign:
										col.field === 'qty'
											? 'center'
											: col.field === 'status'
											? 'right'
											: 'left',
									fontWeight: 800,
									fontSize: `${scaleValue(20)}px`,
								})}
							/>
						</Grid>
					</Page>

					{/* ---------- Page 2: Torque Table (Conditional) ---------- */}
					{showTorqueTable && (
						<Page>
							<StyledTable
								columns={columns}
								rows={torqueData}
								sx={{
									flex: 1,
									tableColors: {
										headerText: themeMode === DARK_MODE ? '#F8F8F8' : '#202020',
										headerBackground: 'transparent',
									},
								}}
							/>
						</Page>
					)}
				</ScrollContainer>
			</Box>
		</ModalDialog>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(RescanModal);
